import { AriaAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import {
  AdminName,
  Content,
  courseCardClasses,
  CourseCode,
  CourseTitle,
  Dates,
  Footer,
  Header,
  Role,
  Root,
  StyledZoomUsIcon,
} from './styles';
import { i18nNS } from '../../../i18n';
import Tooltip from '../../../components/Tooltip';
import { Skeleton, Stack } from '@mui/material';
import UserAvatarSkeleton from '../../../components/UserAvatar/Skeleton';
import CalendarIcon from '../../../icons/CalendarIcon';
import { Course } from '../../../db/courses/types';

export interface Props extends AriaAttributes {
  className?: string;
  classes?: Partial<typeof courseCardClasses>;
  course: Course;
}

const CourseCard = ({ className, classes, course, ...restProps }: Props) => {
  const { t } = useTranslation([i18nNS.COMMON, i18nNS.GLOSSARY]);

  return (
    <Root className={clsx(className, courseCardClasses.root, classes?.root)} {...restProps}>
      <Header className={clsx(courseCardClasses.header, classes?.header)}>
        {course.isPro && (
          <Tooltip placement="top" title={t('zoom_enabled_course', { ns: i18nNS.COMMON }) || ''}>
            <StyledZoomUsIcon className={clsx(courseCardClasses.zoomUsIcon, classes?.zoomUsIcon)} />
          </Tooltip>
        )}
        <CourseCode className={clsx(courseCardClasses.courseCode, classes?.courseCode)}>
          {course.code}
        </CourseCode>
      </Header>
      <Content className={clsx(courseCardClasses.content, classes?.content)}>
        <CourseTitle className={clsx(courseCardClasses.courseTitle, classes?.courseTitle)}>
          {course.title}
        </CourseTitle>
        <Stack direction="row" alignItems="center" gap={2.5}>
          <UserAvatarSkeleton
            className={clsx(courseCardClasses.userAvatarSkeleton, classes?.userAvatarSkeleton)}
            height={18}
            width={18}
          />
          <AdminName>
            <Skeleton
              className={clsx(courseCardClasses.adminNameSkeleton, classes?.adminNameSkeleton)}
              variant="text"
              width={100}
            />
          </AdminName>
        </Stack>
      </Content>
      <Footer className={clsx(courseCardClasses.footer, classes?.footer)}>
        <CalendarIcon
          className={clsx(courseCardClasses.calendarIcon, classes?.calendarIcon)}
          color="primary"
          size={18}
        />
        <Dates>
          <Skeleton variant="text" width={100} />
        </Dates>
        <Role
          className={clsx(courseCardClasses.role, classes?.role)}
          color={'primary'}
          label={t('student', { ns: i18nNS.GLOSSARY })}
          variant="outlined"
        />
      </Footer>
    </Root>
  );
};

export default CourseCard;
